import { lazy, Suspense } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Element as HastElement, ElementContent } from 'hast';
import { MermaidBlock } from './MermaidBlock';

// Lazy on purpose: the proposal card navigates within a workspace
// (useWorkspacePath → OrgContext → tenant api client). Keeping it out of the
// static graph lets the ops bundle reuse Markdown without pulling in tenant
// auth/org code (packages/web/src/ops/boundary.test.ts walks static imports).
const IssueProposalCard = lazy(() =>
  import('./IssueProposalCard').then((m) => ({ default: m.IssueProposalCard })),
);

/** If this <pre> wraps a ```mermaid fence, return the chart source. */
function mermaidChart(node: HastElement | undefined): string | null {
  const child = node?.children?.[0];
  if (!child || child.type !== 'element' || child.tagName !== 'code') return null;
  const className = child.properties?.className;
  if (!Array.isArray(className) || !className.includes('language-mermaid')) return null;
  return child.children
    .map((c: ElementContent) => (c.type === 'text' ? c.value : ''))
    .join('');
}

/** If this <pre> wraps an ```issue-proposal fence, parse and return {title, body}. */
function issueProposal(
  node: HastElement | undefined,
): { title: string; body: string } | null {
  const child = node?.children?.[0];
  if (!child || child.type !== 'element' || child.tagName !== 'code') return null;
  const className = child.properties?.className;
  if (!Array.isArray(className) || !className.includes('language-issue-proposal'))
    return null;
  const raw = child.children
    .map((c: ElementContent) => (c.type === 'text' ? c.value : ''))
    .join('');
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'title' in parsed &&
      'body' in parsed &&
      typeof (parsed as Record<string, unknown>).title === 'string' &&
      typeof (parsed as Record<string, unknown>).body === 'string'
    ) {
      return {
        title: (parsed as { title: string; body: string }).title,
        body: (parsed as { title: string; body: string }).body,
      };
    }
  } catch {
    // malformed JSON — fall through to default rendering
  }
  return null;
}

/** Renders a markdown string with GFM support inside our prose styling. */
export function Markdown({
  children,
  className = '',
  projectId,
}: {
  children: string;
  className?: string;
  projectId?: string;
}) {
  return (
    <div className={`prose-aiai ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer noopener" />
          ),
          table: ({ node: _node, ...props }) => (
            <div className="overflow-x-auto">
              <table {...props} />
            </div>
          ),
          pre: ({ node, ...props }) => {
            const chart = mermaidChart(node);
            if (chart !== null) return <MermaidBlock chart={chart} />;
            if (projectId) {
              const proposal = issueProposal(node);
              if (proposal !== null)
                return (
                  <Suspense fallback={<pre {...props} />}>
                    <IssueProposalCard
                      title={proposal.title}
                      body={proposal.body}
                      projectId={projectId}
                    />
                  </Suspense>
                );
            }
            return <pre {...props} />;
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
